import { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import {
  ContactRound, Loader2, Plus, Save, Trash2, X, Mail, Phone, MapPin,
  Pencil, Globe, Calendar, Building2, Search, MessageSquare, Star, Filter,
} from "lucide-react";
import {
  listCompanies, createCompany, getCompany, updateCompany, deleteCompany,
  createContact, updateContact, deleteContact, logActivity, deleteActivity,
  listAllContacts,
  STATUSES, ACTIVITY_TYPES,
} from "../../../lib/crm";
import { listStores } from "../../../lib/emailTemplates";
import { adminClient } from "../../../lib/admin";

const EMPTY_COMPANY = {
  name: "", store_id: "", industry: "", status: "prospecting",
  website: "", address: "", email: "", phone: "", notes: "",
};
const EMPTY_CONTACT = {
  first_name: "", last_name: "", title: "", email: "", phone: "",
  is_primary: false, notes: "",
};
const EMPTY_ACTIVITY = { type: "call", occurred_at: "", notes: "", contact_id: "" };

const StatusPill = ({ status }) => {
  const meta = STATUSES.find((s) => s.value === status) || STATUSES[0];
  return (
    <span
      className="px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-[0.14em]"
      style={{ background: meta.bg, color: meta.color, border: `1px solid ${meta.color}33` }}
      data-testid={`crm-status-${status}`}
    >
      {meta.label}
    </span>
  );
};

const fmtDate = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
  catch { return ""; }
};

const CRMTab = () => {
  const [stores, setStores] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("companies"); // 'companies' | 'contacts'

  // Filters
  const [storeFilter, setStoreFilter] = useState("");
  const [industryFilter, setIndustryFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [searchQ, setSearchQ] = useState("");
  const [searchInput, setSearchInput] = useState("");

  // Modals
  const [showCreate, setShowCreate] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const params = {};
      if (storeFilter) params.store_id = storeFilter;
      if (industryFilter) params.industry = industryFilter;
      if (statusFilter) params.status = statusFilter;
      if (searchQ.trim()) params.q = searchQ.trim();
      if (viewMode === "companies") {
        const data = await listCompanies(params);
        setCompanies(data.items || []);
        setSummary(data.summary || null);
      } else {
        // Contacts view — fetch the flat list AND keep the global summary up-to-date
        const [contactsData, companiesData] = await Promise.all([
          listAllContacts(params),
          listCompanies({}),
        ]);
        setContacts(contactsData.items || []);
        setSummary(companiesData.summary || null);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Load failed");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    (async () => {
      try {
        const [s, settings] = await Promise.all([
          listStores(),
          adminClient().get("/admin/portfolio/settings").then((r) => r.data),
        ]);
        setStores(s.filter((x) => !x.archived));
        setIndustries((settings.industries || []).filter((i) => !i.archived));
      } catch (e) { /* silent */ }
    })();
  }, []);

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [storeFilter, industryFilter, statusFilter, searchQ, viewMode]);

  const storeMap = useMemo(() => Object.fromEntries(stores.map((s) => [s.id, s])), [stores]);
  const indMap = useMemo(() => Object.fromEntries(industries.map((i) => [i.slug, i])), [industries]);

  return (
    <div className="space-y-4" data-testid="admin-crm-tab">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-2xl font-bold flex items-center gap-2">
            <ContactRound size={20} /> CRM — B2B Contacts
          </h2>
          <p className="text-xs text-[color:var(--ups-subtext)] mt-1">
            {summary?.total ?? "…"} companies · {summary?.by_status?.prospecting || 0} prospecting · {summary?.by_status?.active || 0} active · {summary?.by_status?.inactive || 0} inactive
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="btn-brutal px-3 py-2 text-[12px] font-bold uppercase tracking-[0.14em] rounded-sm flex items-center gap-1.5"
          data-testid="crm-add-company-btn"
        >
          <Plus size={14} /> Add company
        </button>
      </div>

      {/* View-mode toggle */}
      <div className="flex items-center gap-1 p-1 rounded-sm w-fit" style={{ background: "#F5F5F4", border: "1px solid var(--ups-border)" }} data-testid="crm-view-toggle">
        <button
          onClick={() => setViewMode("companies")}
          className="px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.14em] rounded-sm flex items-center gap-1.5"
          style={viewMode === "companies"
            ? { background: "var(--ups-ink)", color: "var(--ups-gold)" }
            : { color: "var(--ups-subtext)" }}
          data-testid="crm-view-companies"
        >
          <Building2 size={12} /> Companies
        </button>
        <button
          onClick={() => setViewMode("contacts")}
          className="px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.14em] rounded-sm flex items-center gap-1.5"
          style={viewMode === "contacts"
            ? { background: "var(--ups-ink)", color: "var(--ups-gold)" }
            : { color: "var(--ups-subtext)" }}
          data-testid="crm-view-contacts"
        >
          <ContactRound size={12} /> Contacts
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 p-3 rounded-sm" style={{ background: "var(--ups-muted)", border: "1px solid var(--ups-border)" }}>
        <Filter size={14} className="text-[color:var(--ups-subtext)]" />
        <select value={storeFilter} onChange={(e) => setStoreFilter(e.target.value)} className="input-swiss px-2 py-1.5 rounded-sm text-[12px]" data-testid="crm-store-filter">
          <option value="">All stores</option>
          {stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <select value={industryFilter} onChange={(e) => setIndustryFilter(e.target.value)} className="input-swiss px-2 py-1.5 rounded-sm text-[12px]" data-testid="crm-industry-filter">
          <option value="">All industries</option>
          {industries.map((i) => <option key={i.slug} value={i.slug}>{i.label}</option>)}
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="input-swiss px-2 py-1.5 rounded-sm text-[12px]" data-testid="crm-status-filter">
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <form
          onSubmit={(e) => { e.preventDefault(); setSearchQ(searchInput); }}
          className="flex items-center gap-1 ml-auto"
        >
          <div className="relative">
            <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-[color:var(--ups-subtext)]" />
            <input
              type="text"
              placeholder={viewMode === "companies" ? "Search company name…" : "Search contact name, email, title…"}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="input-swiss pl-7 pr-2 py-1.5 rounded-sm text-[12px] w-[200px]"
              data-testid="crm-search-input"
            />
          </div>
          {(searchQ || searchInput) && (
            <button type="button" onClick={() => { setSearchInput(""); setSearchQ(""); }} className="text-[11px] font-bold uppercase tracking-[0.14em] px-2 py-1 hover:underline">
              clear
            </button>
          )}
        </form>
      </div>

      {/* List body — Companies or Contacts depending on viewMode */}
      {loading ? (
        <div className="py-12 flex justify-center"><Loader2 className="animate-spin" /></div>
      ) : viewMode === "companies" ? (
        companies.length === 0 ? (
          <div className="p-10 text-center rounded-sm text-[color:var(--ups-subtext)]" style={{ background: "#fff", border: "1px dashed var(--ups-border)" }}>
            No companies yet — click <strong>Add company</strong> to add your first B2B contact.
          </div>
        ) : (
          <div className="space-y-1">
            {companies.map((c) => {
              const store = storeMap[c.store_id];
              const ind = indMap[c.industry];
              return (
                <button
                  key={c.id}
                  onClick={() => setSelectedId(c.id)}
                  className="w-full p-3 flex items-center gap-3 text-left hover:bg-[color:var(--ups-muted)] rounded-sm"
                  style={{ background: "#fff", border: "1px solid var(--ups-border)" }}
                  data-testid={`crm-company-row-${c.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-[14px] truncate">{c.name}</span>
                      <StatusPill status={c.status} />
                    </div>
                    <div className="flex items-center gap-3 text-[11px] text-[color:var(--ups-subtext)] mt-0.5 font-mono">
                      {store && <span className="flex items-center gap-1"><Building2 size={10} /> {store.name}</span>}
                      {ind && <span>· {ind.label}</span>}
                      <span>· {c.contact_count} contact{c.contact_count === 1 ? "" : "s"}</span>
                      {c.last_activity_at && (
                        <span className="flex items-center gap-1">· last touch {fmtDate(c.last_activity_at)} ({c.last_activity_type})</span>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )
      ) : (
        contacts.length === 0 ? (
          <div className="p-10 text-center rounded-sm text-[color:var(--ups-subtext)]" style={{ background: "#fff", border: "1px dashed var(--ups-border)" }} data-testid="crm-contacts-empty">
            No contacts match these filters. Add contacts from a company&apos;s drawer (Companies view &rarr; click a row &rarr; Add contact).
          </div>
        ) : (
          <div className="space-y-1" data-testid="crm-contacts-list">
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[color:var(--ups-subtext)] px-2">
              {contacts.length} contact{contacts.length === 1 ? "" : "s"}
            </div>
            {contacts.map((c) => {
              const store = storeMap[c.company?.store_id];
              const ind = indMap[c.company?.industry];
              return (
                <button
                  key={c.id}
                  onClick={() => setSelectedId(c.company?.id)}
                  className="w-full p-3 flex items-center gap-3 text-left hover:bg-[color:var(--ups-muted)] rounded-sm"
                  style={{ background: "#fff", border: "1px solid var(--ups-border)" }}
                  data-testid={`crm-contact-row-flat-${c.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-[14px] truncate">
                        {c.first_name} {c.last_name}
                      </span>
                      {c.is_primary && <Star size={11} fill="#FFB500" stroke="#1C1917" />}
                      {c.title && <span className="text-[11px] text-[color:var(--ups-subtext)]">· {c.title}</span>}
                      <StatusPill status={c.company?.status} />
                    </div>
                    <div className="flex items-center gap-3 text-[11px] text-[color:var(--ups-subtext)] mt-0.5 font-mono flex-wrap">
                      <span className="flex items-center gap-1 font-bold" style={{ color: "var(--ups-ink)" }}>
                        <Building2 size={10} /> {c.company?.name}
                      </span>
                      {store && <span>· {store.name}</span>}
                      {ind && <span>· {ind.label}</span>}
                      {c.email && <span className="flex items-center gap-1"><Mail size={10} /> {c.email}</span>}
                      {c.phone && <span className="flex items-center gap-1"><Phone size={10} /> {c.phone}</span>}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )
      )}

      {showCreate && (
        <CompanyFormModal
          mode="create"
          stores={stores}
          industries={industries}
          onClose={() => setShowCreate(false)}
          onSaved={async () => { setShowCreate(false); await reload(); }}
        />
      )}

      {selectedId && (
        <CompanyDetailDrawer
          companyId={selectedId}
          stores={stores}
          industries={industries}
          storeMap={storeMap}
          indMap={indMap}
          onClose={() => setSelectedId(null)}
          onChanged={reload}
        />
      )}
    </div>
  );
};

/* =================== Company create/edit modal =================== */
const CompanyFormModal = ({ mode, initial, stores, industries, onClose, onSaved }) => {
  const [draft, setDraft] = useState({ ...EMPTY_COMPANY, ...(initial || {}) });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!draft.name.trim()) { toast.error("Company name is required"); return; }
    if (!draft.store_id) { toast.error("Store is required"); return; }
    setBusy(true);
    try {
      if (mode === "create") await createCompany(draft);
      else await updateCompany(initial.id, draft);
      toast.success(mode === "create" ? "Company added" : "Saved");
      await onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="crm-company-form-modal">
      <button className="absolute inset-0 bg-black/50" onClick={busy ? undefined : onClose} aria-label="Close" />
      <div className="relative w-full max-w-lg rounded-sm overflow-hidden" style={{ background: "#fff", border: "2px solid var(--ups-ink)" }}>
        <div className="flex items-center justify-between px-4 py-3" style={{ background: "var(--ups-ink)", color: "#FFB500" }}>
          <h3 className="font-display text-[18px] font-bold flex items-center gap-2">
            <Building2 size={18} /> {mode === "create" ? "New company" : "Edit company"}
          </h3>
          <button onClick={onClose} className="p-1.5 rounded-sm hover:bg-white/10" aria-label="Close"><X size={16} /></button>
        </div>
        <div className="p-4 max-h-[70vh] overflow-y-auto space-y-3">
          <label className="block">
            <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)]">Company name *</span>
            <input type="text" autoFocus value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              className="input-swiss w-full px-2 py-1.5 mt-0.5 rounded-sm text-[14px] font-bold"
              data-testid="crm-company-name-input" />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)]">Store *</span>
              <select value={draft.store_id} onChange={(e) => setDraft({ ...draft, store_id: e.target.value })}
                className="input-swiss w-full px-2 py-1.5 mt-0.5 rounded-sm text-[13px]"
                data-testid="crm-company-store-select">
                <option value="">— Pick store —</option>
                {stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)]">Industry</span>
              <select value={draft.industry} onChange={(e) => setDraft({ ...draft, industry: e.target.value })}
                className="input-swiss w-full px-2 py-1.5 mt-0.5 rounded-sm text-[13px]"
                data-testid="crm-company-industry-select">
                <option value="">— None —</option>
                {industries.map((i) => <option key={i.slug} value={i.slug}>{i.label}</option>)}
              </select>
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)]">Status</span>
              <select value={draft.status} onChange={(e) => setDraft({ ...draft, status: e.target.value })}
                className="input-swiss w-full px-2 py-1.5 mt-0.5 rounded-sm text-[13px]"
                data-testid="crm-company-status-select">
                {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)]">Website</span>
              <input type="text" value={draft.website} onChange={(e) => setDraft({ ...draft, website: e.target.value })}
                placeholder="https://"
                className="input-swiss w-full px-2 py-1.5 mt-0.5 rounded-sm text-[13px]" />
            </label>
          </div>
          <label className="block">
            <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)]">Address</span>
            <input type="text" value={draft.address} onChange={(e) => setDraft({ ...draft, address: e.target.value })}
              className="input-swiss w-full px-2 py-1.5 mt-0.5 rounded-sm text-[13px]" />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)]">Company email</span>
              <input type="email" value={draft.email} onChange={(e) => setDraft({ ...draft, email: e.target.value })}
                placeholder="info@company.com"
                className="input-swiss w-full px-2 py-1.5 mt-0.5 rounded-sm text-[13px]"
                data-testid="crm-company-email-input" />
            </label>
            <label className="block">
              <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)]">Company phone</span>
              <input type="tel" value={draft.phone} onChange={(e) => setDraft({ ...draft, phone: e.target.value })}
                placeholder="+1 555 123 4567"
                className="input-swiss w-full px-2 py-1.5 mt-0.5 rounded-sm text-[13px]"
                data-testid="crm-company-phone-input" />
            </label>
          </div>
          <label className="block">
            <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)]">Notes</span>
            <textarea value={draft.notes} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} rows={3}
              className="input-swiss w-full px-2 py-1.5 mt-0.5 rounded-sm text-[12px] resize-none" />
          </label>
        </div>
        <div className="px-4 py-3 flex justify-end gap-2" style={{ borderTop: "1px solid var(--ups-border)" }}>
          <button onClick={onClose} className="px-3 py-2 text-[12px] font-bold uppercase tracking-[0.14em] rounded-sm hover:bg-[color:var(--ups-muted)]" style={{ border: "1px solid var(--ups-border)" }}>Cancel</button>
          <button onClick={submit} disabled={busy} className="btn-brutal px-4 py-2 text-[12px] font-bold uppercase tracking-[0.14em] rounded-sm flex items-center gap-1.5 disabled:opacity-50" data-testid="crm-company-save-btn">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
          </button>
        </div>
      </div>
    </div>
  );
};

/* =================== Company detail drawer =================== */
const CompanyDetailDrawer = ({ companyId, stores, industries, storeMap, indMap, onClose, onChanged }) => {
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [addContact, setAddContact] = useState(false);
  const [editContact, setEditContact] = useState(null);
  const [contactDraft, setContactDraft] = useState(EMPTY_CONTACT);
  const [activityDraft, setActivityDraft] = useState({ ...EMPTY_ACTIVITY, occurred_at: new Date().toISOString().slice(0,10) });
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try {
      setCompany(await getCompany(companyId));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Load failed");
      onClose();
    } finally { setLoading(false); }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [companyId]);

  const handleStatusChange = async (status) => {
    try {
      await updateCompany(companyId, { status });
      toast.success(`Marked ${status}`);
      await refresh();
      onChanged();
    } catch (e) { toast.error("Status change failed"); }
  };

  const handleAddContact = async () => {
    if (!contactDraft.first_name.trim()) { toast.error("First name required"); return; }
    setBusy(true);
    try {
      await createContact(companyId, contactDraft);
      toast.success("Contact added");
      setContactDraft(EMPTY_CONTACT);
      setAddContact(false);
      await refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Add failed"); }
    finally { setBusy(false); }
  };

  const handleUpdateContact = async () => {
    if (!editContact.first_name.trim()) { toast.error("First name required"); return; }
    setBusy(true);
    try {
      await updateContact(editContact.id, editContact);
      toast.success("Saved");
      setEditContact(null);
      await refresh();
    } catch (e) { toast.error("Save failed"); }
    finally { setBusy(false); }
  };

  const handleDeleteContact = async (c) => {
    if (!window.confirm(`Remove contact "${c.first_name} ${c.last_name}"?`)) return;
    try { await deleteContact(c.id); await refresh(); }
    catch (e) { toast.error("Delete failed"); }
  };

  const handleLogActivity = async () => {
    setBusy(true);
    try {
      const payload = { ...activityDraft };
      if (payload.occurred_at) payload.occurred_at = new Date(payload.occurred_at).toISOString();
      if (!payload.contact_id) delete payload.contact_id;
      await logActivity(companyId, payload);
      toast.success("Activity logged");
      setActivityDraft({ type: "call", occurred_at: new Date().toISOString().slice(0,10), notes: "", contact_id: "" });
      await refresh();
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Log failed"); }
    finally { setBusy(false); }
  };

  const handleDeleteActivity = async (a) => {
    if (!window.confirm("Remove this activity entry?")) return;
    try { await deleteActivity(a.id); await refresh(); }
    catch (e) { toast.error("Delete failed"); }
  };

  const handleDeleteCompany = async () => {
    if (!window.confirm(`Delete "${company.name}" and ALL its contacts + activity history?`)) return;
    try {
      await deleteCompany(companyId);
      toast.success("Deleted");
      onClose();
      onChanged();
    } catch (e) { toast.error("Delete failed"); }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="crm-company-drawer">
      <button className="absolute inset-0 bg-black/50" onClick={onClose} aria-label="Close" />
      <div className="relative w-full max-w-2xl h-full overflow-y-auto" style={{ background: "var(--ups-paper)", borderLeft: "2px solid var(--ups-ink)" }}>
        {loading || !company ? (
          <div className="h-full flex items-center justify-center"><Loader2 className="animate-spin" /></div>
        ) : editing ? (
          <CompanyFormModalInline
            company={company}
            stores={stores}
            industries={industries}
            onCancel={() => setEditing(false)}
            onSaved={async () => { setEditing(false); await refresh(); onChanged(); }}
          />
        ) : (
          <>
            {/* Header */}
            <div className="sticky top-0 z-10 px-5 py-4 flex items-start justify-between gap-3" style={{ background: "var(--ups-ink)", color: "#FFB500" }}>
              <div className="flex-1 min-w-0">
                <h3 className="font-display text-2xl font-bold truncate">{company.name}</h3>
                <div className="text-[11px] mt-1 font-mono opacity-80">
                  {storeMap[company.store_id]?.name}
                  {company.industry && indMap[company.industry] && <> · {indMap[company.industry].label}</>}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => setEditing(true)} className="p-1.5 rounded-sm hover:bg-white/10" title="Edit" data-testid="crm-drawer-edit-btn"><Pencil size={14} /></button>
                <button onClick={handleDeleteCompany} className="p-1.5 rounded-sm hover:bg-white/10" title="Delete" data-testid="crm-drawer-delete-btn"><Trash2 size={14} /></button>
                <button onClick={onClose} className="p-1.5 rounded-sm hover:bg-white/10" aria-label="Close"><X size={16} /></button>
              </div>
            </div>

            <div className="p-5 space-y-5">
              {/* Status switcher */}
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[color:var(--ups-subtext)]">Status:</span>
                {STATUSES.map((s) => (
                  <button
                    key={s.value}
                    onClick={() => handleStatusChange(s.value)}
                    className="px-2 py-1 text-[11px] font-bold uppercase tracking-[0.14em] rounded-sm"
                    style={company.status === s.value
                      ? { background: s.bg, color: s.color, border: `2px solid ${s.color}` }
                      : { background: "#fff", border: "1px solid var(--ups-border)" }}
                    data-testid={`crm-drawer-status-${s.value}`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>

              {/* Company metadata */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[12px]" data-testid="crm-drawer-company-meta">
                {company.email && (
                  <div className="flex items-center gap-2"><Mail size={12} className="text-[color:var(--ups-subtext)]" />
                    <a href={`mailto:${company.email}`} className="hover:underline truncate font-mono" data-testid="crm-drawer-company-email">{company.email}</a>
                  </div>
                )}
                {company.phone && (
                  <div className="flex items-center gap-2"><Phone size={12} className="text-[color:var(--ups-subtext)]" />
                    <a href={`tel:${company.phone}`} className="hover:underline truncate font-mono" data-testid="crm-drawer-company-phone">{company.phone}</a>
                  </div>
                )}
                {company.website && (
                  <div className="flex items-center gap-2"><Globe size={12} className="text-[color:var(--ups-subtext)]" />
                    <a href={company.website} target="_blank" rel="noopener noreferrer" className="hover:underline truncate">{company.website}</a>
                  </div>
                )}
                {company.address && (
                  <div className="flex items-center gap-2"><MapPin size={12} className="text-[color:var(--ups-subtext)]" /> <span className="truncate">{company.address}</span></div>
                )}
              </div>
              {company.notes && (
                <p className="text-[12px] p-3 rounded-sm" style={{ background: "#fff", border: "1px solid var(--ups-border)" }}>{company.notes}</p>
              )}

              {/* Contacts */}
              <section>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-display text-[14px] font-bold uppercase tracking-[0.12em] flex items-center gap-1.5">
                    <ContactRound size={14} /> Contacts ({company.contacts.length})
                  </h4>
                  <button onClick={() => setAddContact(true)} className="text-[11px] font-bold uppercase tracking-[0.14em] flex items-center gap-1 hover:underline" data-testid="crm-add-contact-btn">
                    <Plus size={12} /> Add contact
                  </button>
                </div>

                {addContact && (
                  <ContactInlineForm
                    draft={contactDraft}
                    setDraft={setContactDraft}
                    onCancel={() => { setAddContact(false); setContactDraft(EMPTY_CONTACT); }}
                    onSave={handleAddContact}
                    busy={busy}
                  />
                )}

                {company.contacts.length === 0 ? (
                  <div className="p-4 rounded-sm text-[12px] text-[color:var(--ups-subtext)] text-center" style={{ background: "#fff", border: "1px dashed var(--ups-border)" }}>
                    No contacts yet.
                  </div>
                ) : (
                  <div className="space-y-1">
                    {company.contacts.map((c) => (
                      <div key={c.id} className="p-3 rounded-sm" style={{ background: "#fff", border: "1px solid var(--ups-border)" }} data-testid={`crm-contact-row-${c.id}`}>
                        {editContact?.id === c.id ? (
                          <ContactInlineForm
                            draft={editContact}
                            setDraft={setEditContact}
                            onCancel={() => setEditContact(null)}
                            onSave={handleUpdateContact}
                            busy={busy}
                          />
                        ) : (
                          <div className="flex items-start gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-bold text-[14px]">{c.first_name} {c.last_name}</span>
                                {c.is_primary && <Star size={12} fill="#FFB500" stroke="#1C1917" />}
                                {c.title && <span className="text-[11px] text-[color:var(--ups-subtext)]">{c.title}</span>}
                              </div>
                              <div className="flex flex-wrap gap-3 mt-1 text-[12px] font-mono">
                                {c.email && (
                                  <a href={`mailto:${c.email}`} className="flex items-center gap-1 hover:underline">
                                    <Mail size={11} /> {c.email}
                                  </a>
                                )}
                                {c.phone && (
                                  <a href={`tel:${c.phone}`} className="flex items-center gap-1 hover:underline">
                                    <Phone size={11} /> {c.phone}
                                  </a>
                                )}
                              </div>
                              {c.notes && <div className="text-[11px] text-[color:var(--ups-subtext)] mt-1">{c.notes}</div>}
                            </div>
                            <div className="flex items-center gap-1">
                              <button onClick={() => setEditContact(c)} className="p-1 rounded-sm hover:bg-[color:var(--ups-muted)]" title="Edit"><Pencil size={11} /></button>
                              <button onClick={() => handleDeleteContact(c)} className="p-1 rounded-sm hover:bg-red-50 text-red-700" title="Delete"><Trash2 size={11} /></button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </section>

              {/* Log activity */}
              <section>
                <h4 className="font-display text-[14px] font-bold uppercase tracking-[0.12em] flex items-center gap-1.5 mb-2">
                  <MessageSquare size={14} /> Log activity
                </h4>
                <div className="p-3 rounded-sm" style={{ background: "#FFF8E1", border: "1px solid #FFB500" }}>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <select value={activityDraft.type} onChange={(e) => setActivityDraft({ ...activityDraft, type: e.target.value })}
                      className="input-swiss px-2 py-1.5 rounded-sm text-[13px]" data-testid="crm-activity-type">
                      {ACTIVITY_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                    <input type="date" value={activityDraft.occurred_at} onChange={(e) => setActivityDraft({ ...activityDraft, occurred_at: e.target.value })}
                      className="input-swiss px-2 py-1.5 rounded-sm text-[13px]" data-testid="crm-activity-date" />
                    <select value={activityDraft.contact_id} onChange={(e) => setActivityDraft({ ...activityDraft, contact_id: e.target.value })}
                      className="input-swiss px-2 py-1.5 rounded-sm text-[13px]" data-testid="crm-activity-contact">
                      <option value="">— With which contact? —</option>
                      {company.contacts.map((c) => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
                    </select>
                  </div>
                  <textarea
                    value={activityDraft.notes}
                    onChange={(e) => setActivityDraft({ ...activityDraft, notes: e.target.value })}
                    placeholder="What happened? (left a voicemail, sent quote, scheduled demo…)"
                    rows={2}
                    className="input-swiss w-full px-2 py-1.5 mt-2 rounded-sm text-[12px] resize-none"
                    data-testid="crm-activity-notes"
                  />
                  <div className="flex justify-end mt-2">
                    <button onClick={handleLogActivity} disabled={busy} className="btn-brutal px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.14em] rounded-sm flex items-center gap-1 disabled:opacity-50" data-testid="crm-activity-log-btn">
                      {busy ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />} Log it
                    </button>
                  </div>
                </div>
              </section>

              {/* Activity timeline */}
              <section>
                <h4 className="font-display text-[14px] font-bold uppercase tracking-[0.12em] flex items-center gap-1.5 mb-2">
                  <Calendar size={14} /> Activity log ({company.activities.length})
                </h4>
                {company.activities.length === 0 ? (
                  <div className="p-4 rounded-sm text-[12px] text-[color:var(--ups-subtext)] text-center" style={{ background: "#fff", border: "1px dashed var(--ups-border)" }}>
                    No activity logged yet.
                  </div>
                ) : (
                  <div className="space-y-1">
                    {company.activities.map((a) => {
                      const ct = ACTIVITY_TYPES.find((t) => t.value === a.type) || { label: a.type };
                      const contact = company.contacts.find((c) => c.id === a.contact_id);
                      return (
                        <div key={a.id} className="p-2.5 rounded-sm group" style={{ background: "#fff", border: "1px solid var(--ups-border)" }} data-testid={`crm-activity-row-${a.id}`}>
                          <div className="flex items-start gap-2">
                            <div className="text-[10px] font-bold uppercase tracking-[0.18em] px-1.5 py-0.5 rounded-sm flex-shrink-0" style={{ background: "var(--ups-ink)", color: "var(--ups-gold)" }}>{ct.label}</div>
                            <div className="flex-1 min-w-0">
                              <div className="text-[11px] text-[color:var(--ups-subtext)] font-mono">
                                {fmtDate(a.occurred_at)}
                                {contact && <> · with {contact.first_name} {contact.last_name}</>}
                              </div>
                              {a.notes && <div className="text-[12px] mt-1 whitespace-pre-wrap">{a.notes}</div>}
                            </div>
                            <button onClick={() => handleDeleteActivity(a)} className="p-1 rounded-sm hover:bg-red-50 text-red-700 opacity-0 group-hover:opacity-100 transition" title="Delete entry">
                              <Trash2 size={10} />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

/* Inline edit form for the drawer's edit mode */
const CompanyFormModalInline = ({ company, stores, industries, onCancel, onSaved }) => (
  <CompanyFormModal
    mode="edit"
    initial={company}
    stores={stores}
    industries={industries}
    onClose={onCancel}
    onSaved={onSaved}
  />
);

/* Contact inline create/edit form */
const ContactInlineForm = ({ draft, setDraft, onCancel, onSave, busy }) => (
  <div className="p-3 rounded-sm" style={{ background: "#FFF8E1", border: "2px solid #FFB500" }}>
    <div className="grid grid-cols-2 gap-2 mb-2">
      <input type="text" placeholder="First name *" value={draft.first_name} onChange={(e) => setDraft({ ...draft, first_name: e.target.value })}
        className="input-swiss px-2 py-1.5 rounded-sm text-[13px]" data-testid="crm-contact-first-name" autoFocus />
      <input type="text" placeholder="Last name" value={draft.last_name} onChange={(e) => setDraft({ ...draft, last_name: e.target.value })}
        className="input-swiss px-2 py-1.5 rounded-sm text-[13px]" />
    </div>
    <input type="text" placeholder="Title" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })}
      className="input-swiss w-full px-2 py-1.5 rounded-sm text-[13px] mb-2" />
    <div className="grid grid-cols-2 gap-2 mb-2">
      <input type="email" placeholder="Email" value={draft.email} onChange={(e) => setDraft({ ...draft, email: e.target.value })}
        className="input-swiss px-2 py-1.5 rounded-sm text-[13px]" data-testid="crm-contact-email" />
      <input type="tel" placeholder="Phone" value={draft.phone} onChange={(e) => setDraft({ ...draft, phone: e.target.value })}
        className="input-swiss px-2 py-1.5 rounded-sm text-[13px]" />
    </div>
    <textarea placeholder="Notes" value={draft.notes} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} rows={2}
      className="input-swiss w-full px-2 py-1.5 rounded-sm text-[12px] resize-none mb-2" />
    <label className="flex items-center gap-2 text-[11px] mb-3 cursor-pointer">
      <input type="checkbox" checked={!!draft.is_primary} onChange={(e) => setDraft({ ...draft, is_primary: e.target.checked })} />
      Primary contact for this company
    </label>
    <div className="flex justify-end gap-1">
      <button onClick={onCancel} className="px-2 py-1 text-[10px] font-bold uppercase tracking-[0.14em] rounded-sm hover:bg-white" style={{ border: "1px solid var(--ups-border)" }}>Cancel</button>
      <button onClick={onSave} disabled={busy} className="btn-brutal px-3 py-1 text-[10px] font-bold uppercase tracking-[0.14em] rounded-sm flex items-center gap-1 disabled:opacity-50" data-testid="crm-contact-save-btn">
        {busy ? <Loader2 size={10} className="animate-spin" /> : <Save size={10} />} Save
      </button>
    </div>
  </div>
);

export default CRMTab;
